import type {
  StudioPersonActivityEntry,
  StudioPersonActivityQuery,
  StudioPersonProject,
  StudioPersonSignIn,
  StudioPersonSummary,
  StudioTeamPerson,
} from '@/api/adminClient';

import { userDetailPath } from './usersModel';

/** Every kind of activity row, with the words the filter and the rows use. */
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  prompt_sent: 'Prompt',
  plan_answered: 'Plan answered',
  plan_approved: 'Plan approved',
  code_edited: 'Code edited',
  version_restored: 'Version restored',
  version_discarded: 'Version discarded',
  project_created: 'Project created',
  project_deleted: 'Project deleted',
  project_renamed: 'Project renamed',
  repo_imported: 'Repository imported',
  github_synced: 'GitHub',
  source_changed: 'Source',
  template_submitted: 'Template submitted',
  app_published: 'Published',
  integration_changed: 'Integration',
  secret_changed: 'Secret',
  api_integration_changed: 'API integration',
  api_key_changed: 'API key',
  webhook_changed: 'Webhook',
  app_settings_changed: 'App settings',
  app_access_changed: 'App access',
  org_member_changed: 'Organization member',
  data_deleted: 'Data deleted',
  skill_changed: 'Skill',
  role_changed: 'Studio role',
  support_view: 'Support view',
  person_viewed: 'Viewed a person',
  active: 'Used Studio',
};

/** The type filter, grouped the way people think about it. */
export const ACTIVITY_TYPE_GROUPS: Array<{ label: string; types: string[] }> = [
  { label: 'Building', types: ['prompt_sent', 'plan_answered', 'plan_approved', 'code_edited', 'version_restored', 'version_discarded'] },
  { label: 'Projects', types: ['project_created', 'project_deleted', 'project_renamed', 'repo_imported', 'github_synced', 'source_changed', 'template_submitted', 'app_published'] },
  { label: 'Integrations & secrets', types: ['integration_changed', 'secret_changed', 'api_integration_changed', 'api_key_changed', 'webhook_changed'] },
  { label: 'Access & settings', types: ['app_access_changed', 'org_member_changed', 'app_settings_changed', 'skill_changed', 'data_deleted'] },
  { label: 'Studio Admin & support', types: ['role_changed', 'support_view', 'person_viewed'] },
  { label: 'Presence', types: ['active'] },
];

export function activityTypeLabel(type: string): string {
  return ACTIVITY_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

export const PERSON_TABS = ['activity', 'organizations', 'projects', 'sign-ins'] as const;
export type PersonTab = (typeof PERSON_TABS)[number];

export function personTabFrom(value: string | null): PersonTab {
  return (PERSON_TABS as readonly string[]).includes(value ?? '') ? (value as PersonTab) : 'activity';
}

/** The activity filters kept in the address, so a filtered view can be shared. */
export function activityQueryFrom(params: URLSearchParams): StudioPersonActivityQuery {
  const type = params.get('type') || undefined;
  return {
    ...(type && ACTIVITY_TYPE_LABELS[type] ? { type } : {}),
    ...(params.get('project') ? { projectId: params.get('project') as string } : {}),
    ...(params.get('from') ? { from: params.get('from') as string } : {}),
    ...(params.get('to') ? { to: params.get('to') as string } : {}),
    ...(params.get('q') ? { q: params.get('q') as string } : {}),
  };
}

export function activityParams(tab: PersonTab, query: StudioPersonActivityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (tab !== 'activity') params.set('tab', tab);
  if (query.type) params.set('type', query.type);
  if (query.projectId) params.set('project', query.projectId);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.q) params.set('q', query.q);
  return params;
}

/** The API's query string for one page of activity. */
export function activityApiParams(query: StudioPersonActivityQuery, cursor?: string): URLSearchParams {
  const params = new URLSearchParams();
  if (query.type) params.set('types', query.type);
  if (query.projectId) params.set('projectId', query.projectId);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.q) params.set('q', query.q);
  if (cursor) params.set('cursor', cursor);
  return params;
}

export function isFiltered(query: StudioPersonActivityQuery): boolean {
  return Boolean(query.type || query.projectId || query.from || query.to || query.q);
}

/** Where an activity row leads: its run, its project, or the person a role change was about. */
export function entryLink(entry: StudioPersonActivityEntry): { to: string; label: string } | undefined {
  const person = (entry.type === 'role_changed' || entry.type === 'person_viewed') && userDetailPath(entry.targetUserId);
  if (person) return { to: person, label: 'Open person' };
  if (!entry.organizationId) return undefined;
  const org = encodeURIComponent(entry.organizationId);
  if (entry.runId) return { to: `/organizations/${org}/runs/${encodeURIComponent(entry.runId)}`, label: 'Open run' };
  if (entry.projectId) return { to: `/organizations/${org}/projects/${encodeURIComponent(entry.projectId)}`, label: 'Open project' };
  return undefined;
}

/** Where a row came from, when it is not a recorded row: older history Studio already kept. */
export function entrySourceNote(entry: Pick<StudioPersonActivityEntry, 'source'>): string | undefined {
  if (entry.source === 'history') return 'From Studio history';
  if (entry.source === 'project') return 'From the project record';
  if (entry.source === 'role') return 'From the role change log';
  if (entry.source === 'support') return 'From the support access log';
  return undefined;
}

const PROMPT_MODES: Record<string, string> = { build: 'Build', plan: 'Plan', ask: 'Ask', fix: 'Fix', ops: 'Ops' };

export function promptModeLabel(mode: string): string {
  return PROMPT_MODES[mode] ?? mode;
}

const ROLE_LABELS: Record<StudioPersonProject['role'], string> = { owner: 'Owner', admin: 'Admin', member: 'Member', contributor: 'Contributor' };

export function projectRoleLabel(role: StudioPersonProject['role']): string {
  return ROLE_LABELS[role] ?? role;
}

export function signInKindLabel(entry: Pick<StudioPersonSignIn, 'kind' | 'result' | 'count'>): string {
  if (entry.kind === 'sign_in') return 'Signed in';
  if (entry.kind === 'sign_out') return entry.result === 'failure' ? 'Sign-out failed' : 'Signed out';
  if (entry.kind === 'failed') return 'Sign-in failed';
  const times = entry.count && entry.count > 1 ? ` (${entry.count}×)` : '';
  return `${entry.result === 'failure' ? 'Session refresh failed' : 'Session refreshed'}${times}`;
}

export function signInsProblem(result: { configured: boolean }): string | undefined {
  if (!result.configured) return 'Sign-ins come from WorkOS, and WorkOS is not configured on the Studio API.';
  return undefined;
}

export function initials(label: string): string {
  const parts = label.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || '?').slice(0, 2);
}

/**
 * The person as Edit roles takes them. Their own Platform Admin is locked, as
 * on the Users page; the API still refuses removing the last one.
 */
export function teamPersonFrom(summary: StudioPersonSummary): StudioTeamPerson {
  return {
    userId: summary.person.userId,
    ...(summary.person.email ? { email: summary.person.email } : {}),
    ...(summary.person.name ? { name: summary.person.name } : {}),
    ...(summary.person.picture ? { picture: summary.person.picture } : {}),
    internal: true,
    isYou: summary.person.isYou,
    roles: (summary.roles ?? []).map((key) =>
      key === 'platform_admin' && summary.person.isYou ? { key, removable: false, removeBlockedReason: 'self_lockout' as const } : { key, removable: true },
    ),
  };
}
